<template>
  <div class="content">
    <!-- Header: Title + Subtitle + Actions -->
    <div class="header d-flex justify-content-between align-items-center">
      <div class="title ml-3">
        <h3 class="h3 mb-n2 bold">Tipo RH</h3>
        <span class="h6 mt-n2 text-gray-400">
          Classificação de colaboradores por tipo de RH nos projetos
        </span>
      </div>
      <div class="actions d-flex align-items-center">
        <v-text-field
          v-model="search"
          class="mr-3"
          style="max-width: 250px"
          prepend-icon="mdi-magnify"
          placeholder="Buscar..."
          dense
          clearable
          hide-details
        ></v-text-field>

        <v-select
          style="max-width: 170px"
          class="mb-n3 mr-3"
          prepend-icon="mdi-calendar"
          prefix="Ano-base"
          :items="anosBase"
          v-model="selectedAnoBase"
          dense
          @change="doLoad"
        ></v-select>
        <button
          class="btn btn-secondary text-white elevation-3 mr-2"
          @click="doLoad"
        >
          <v-icon left class="text-white">mdi-refresh</v-icon>
          Atualizar
        </button>

        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <button
              class="btn btn-secondary text-white elevation-3 mr-2"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon left class="text-white">mdi-download</v-icon>
              Exportar
              <v-icon right class="text-white">mdi-chevron-down</v-icon>
            </button>
          </template>
          <v-list dense>
            <v-list-item @click="doExport('xlsx')">
              <v-list-item-icon>
                <v-icon left>mdi-file-excel</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="ml-n4">Exportar XLSX</v-list-item-title>
            </v-list-item>
            <v-list-item @click="doExport('csv')">
              <v-list-item-icon>
                <v-icon left>mdi-file-delimited</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="ml-n4">Exportar CSV</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <!-- Body: Content with tables -->
    <div class="body">
      <!-- Section 1: Pending Classification -->
      <div v-if="pendingItems.length > 0" class="mb-5">
        <div class="d-flex align-items-center mb-2 mt-3">
          <v-icon color="warning" class="mr-2">mdi-alert-circle</v-icon>
          <h5 class="h5 mb-0">Pendentes de Classificação ({{ pendingItems.length }})</h5>
        </div>
        <div class="teros-elevation rounded">
          <v-data-table
            v-model="selectedPendingItems"
            class="mb-3 mt-2"
            :headers="pendingHeaders"
            :items="pendingItems"
            :search="search"
            :loading="loading"
            item-key="uniqueKey"
            show-select
            :footer-props="{
              'items-per-page-options': [10, 25, 50, -1],
            }"
          >
            <template v-slot:item.tiporh="{ item }">
              <v-select
                v-model="item.tiporh"
                :items="tipoRHOptions"
                item-text="nome"
                item-value="id"
                dense
                hide-details
                outlined
                style="max-width: 140px"
              ></v-select>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn
                small
                color="primary"
                depressed
                :loading="item.saving"
                @click="createTipoRH(item)"
              >
                Classificar
              </v-btn>
            </template>
          </v-data-table>
        </div>
        <div class="d-flex justify-end mt-2">
          <button
            v-if="selectedPendingItems.length > 0"
            class="btn btn-primary text-white elevation-3"
            @click="openBatchPendingModal"
          >
            <v-icon left class="text-white">mdi-check-all</v-icon>
            Classificar Selecionados ({{ selectedPendingItems.length }})
          </button>
        </div>
      </div>

      <!-- Section 2: Classified -->
      <div>
        <div class="d-flex align-items-center mb-2">
          <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
          <h5 class="h5 mb-0">Classificados ({{ classifiedItems.length }})</h5>
        </div>
        <div class="teros-elevation rounded">
          <v-data-table
            v-model="selectedItems"
            class="mb-7 mt-2"
            :headers="headers"
            :items="classifiedItems"
            :search="search"
            :loading="loading"
            item-key="id"
            show-select
            :footer-props="{
              'items-per-page-options': [10, 25, 50, -1],
            }"
          >
            <template v-slot:item.tiporh="{ item }">
              <v-chip
                :color="item.tiporh === 1 ? 'primary' : item.tiporh === 2 ? 'secondary' : 'grey'"
                small
                dark
              >
                {{ item.tiporh === 1 ? 'Direto' : item.tiporh === 2 ? 'Indireto' : 'Nenhum' }}
              </v-chip>
            </template>
          </v-data-table>
        </div>
        <div class="d-flex justify-end mb-4 mt-n4">
          <button
            v-if="selectedItems.length > 0"
            class="btn btn-primary text-white elevation-3"
            @click="openBatchEditModal"
          >
            <v-icon left class="text-white">mdi-pencil</v-icon>
            Editar Selecionados ({{ selectedItems.length }})
          </button>
        </div>
      </div>
    </div>

    <!-- Batch Edit Modal -->
    <g-modal
      v-model="batchEditModal"
      title="Editar Tipo RH em Lote"
      width="500px"
    >
      <v-container>
        <v-row>
          <v-col cols="12">
            <p class="mb-3">
              Você está editando <strong>{{ selectedItems.length }}</strong> registro(s).
            </p>
            <v-select
              v-model="batchTipoRH"
              :items="tipoRHOptions"
              item-text="nome"
              item-value="id"
              label="Novo Tipo RH"
              outlined
              dense
            ></v-select>
          </v-col>
        </v-row>
      </v-container>

      <template #buttons>
        <v-spacer />
        <v-btn
          color="secondary"
          depressed
          class="px-5"
          @click="batchEditModal = false"
        >
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          dark
          depressed
          class="px-5 ml-3"
          :loading="saving"
          @click="doBatchSave"
        >
          Salvar
        </v-btn>
      </template>
    </g-modal>

    <!-- Batch Pending Classification Modal -->
    <g-modal
      v-model="batchPendingModal"
      title="Classificar Pendentes em Lote"
      width="500px"
    >
      <v-container>
        <v-row>
          <v-col cols="12">
            <p class="mb-3">
              Você está classificando <strong>{{ selectedPendingItems.length }}</strong> colaborador(es).
            </p>
            <v-select
              v-model="batchPendingTipoRH"
              :items="tipoRHOptionsCreate"
              item-text="nome"
              item-value="id"
              label="Tipo RH"
              outlined
              dense
            ></v-select>
          </v-col>
        </v-row>
      </v-container>

      <template #buttons>
        <v-spacer />
        <v-btn
          color="secondary"
          depressed
          class="px-5"
          @click="batchPendingModal = false"
        >
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          dark
          depressed
          class="px-5 ml-3"
          :loading="savingBatchPending"
          @click="createBatchPending"
        >
          Classificar
        </v-btn>
      </template>
    </g-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

export default {
  components: {
    "g-modal": () => import("@/components/g-modal.vue"),
  },
  data() {
    return {
      classifiedItems: [],
      pendingItems: [],
      selectedItems: [],
      selectedPendingItems: [],
      loading: false,
      saving: false,
      savingBatchPending: false,
      search: "",
      selectedAnoBase: new Date().getFullYear(),
      batchEditModal: false,
      batchTipoRH: 1,
      batchPendingModal: false,
      batchPendingTipoRH: 1,
      tipoRHOptions: [
        { id: 0, nome: "Nenhum" },
        { id: 1, nome: "Direto" },
        { id: 2, nome: "Indireto" },
      ],
    };
  },
  computed: {
    ...mapGetters(["clientId", "anoBase"]),
    resource() {
      return this.apiResource(`/v1/rh/${this.clientId}/tiporh`);
    },
    batchResource() {
      return this.apiResource(`/v1/rh/${this.clientId}/tiporh/batch`);
    },
    createResource() {
      return this.apiResource(`/v1/rh/${this.clientId}/tiporh/create`);
    },
    anosBase() {
      const anoAtual = new Date().getFullYear();
      const anos = [];
      for (let ano = anoAtual - 5; ano <= anoAtual; ano++) {
        anos.push(ano);
      }
      return anos;
    },
    tipoRHOptionsCreate() {
      return [
        { id: 0, nome: "Nenhum" },
        { id: 1, nome: "Direto" },
        { id: 2, nome: "Indireto" },
      ];
    },
    headers() {
      return [
        { text: "ID", value: "funcionarioId", width: "80px" },
        { text: "Matrícula", value: "matricula", width: "120px" },
        { text: "Funcionário", value: "nome" },
        { text: "Formação", value: "formacaoli_nome", width: "140px" },
        { text: "Escolaridade", value: "escolaridadeli_nome", width: "150px" },
        { text: "Projeto", value: "titulo" },
        { text: "Tipo RH", value: "tiporh", width: "120px" },
      ];
    },
    pendingHeaders() {
      return [
        { text: "Matrícula", value: "funcionario_matricula", width: "120px" },
        { text: "Funcionário", value: "funcionario_nome" },
        { text: "Formação", value: "formacaoli_nome", width: "140px" },
        { text: "Escolaridade", value: "escolaridadeli_nome", width: "150px" },
        { text: "Projeto", value: "projeto_titulo" },
        { text: "Tipo RH", value: "tiporh", width: "150px" },
        { text: "Ações", value: "actions", width: "120px", sortable: false },
      ];
    },
  },
  created() {
    this.selectedAnoBase = parseInt(this.anoBase) || new Date().getFullYear();
    this.doLoad();
  },
  watch: {
    clientId() {
      this.doLoad();
    },
    anoBase(newVal) {
      const parsed = parseInt(newVal);
      if (this.selectedAnoBase !== parsed) {
        this.selectedAnoBase = parsed;
        this.doLoad();
      }
    },
    selectedAnoBase(newVal) {
      if (this.anoBase !== newVal) {
        this.$store.commit('setAnoBase', newVal);
      }
    },
  },
  methods: {
    async doLoad() {
      this.loading = true;
      this.selectedItems = [];
      this.selectedPendingItems = [];
      try {
        const response = await this.resource.get({
          query: `anoBase=${this.selectedAnoBase}`,
        });
        this.classifiedItems = response.classified || [];
        this.pendingItems = (response.pending || []).map((item, index) => ({
          ...item,
          tiporh: 1, // default to Direto
          saving: false,
          uniqueKey: `${item.projetoId}-${item.funcionarioId}-${index}`,
        }));
      } catch (error) {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "error",
          title: "Erro",
          text: "Erro ao carregar dados.",
        });
      } finally {
        this.loading = false;
      }
    },
    openBatchEditModal() {
      this.batchTipoRH = 1;
      this.batchEditModal = true;
    },
    async doBatchSave() {
      this.saving = true;
      try {
        await this.batchResource.save({
          ids: this.selectedItems.map((item) => item.id),
          tiporh: this.batchTipoRH,
        });
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "success",
          title: "Sucesso",
          text: `${this.selectedItems.length} registro(s) atualizado(s).`,
        });
        this.batchEditModal = false;
        this.doLoad();
      } catch (error) {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "error",
          title: "Erro",
          text: "Erro ao salvar alterações.",
        });
      } finally {
        this.saving = false;
      }
    },
    async createTipoRH(item) {
      item.saving = true;
      try {
        await this.createResource.save({
          empresaId: item.empresaId,
          projetoId: item.projetoId,
          funcionarioId: item.funcionarioId,
          tiporh: item.tiporh,
        });
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "success",
          title: "Sucesso",
          text: "Colaborador classificado com sucesso.",
        });
        this.doLoad();
      } catch (error) {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "error",
          title: "Erro",
          text: "Erro ao classificar colaborador.",
        });
      } finally {
        item.saving = false;
      }
    },
    openBatchPendingModal() {
      this.batchPendingTipoRH = 1;
      this.batchPendingModal = true;
    },
    async createBatchPending() {
      this.savingBatchPending = true;
      try {
        const items = this.selectedPendingItems.map((item) => ({
          empresaId: item.empresaId,
          projetoId: item.projetoId,
          funcionarioId: item.funcionarioId,
          tiporh: this.batchPendingTipoRH,
        }));
        await this.createResource.save({ items });
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "success",
          title: "Sucesso",
          text: `${this.selectedPendingItems.length} colaborador(es) classificado(s).`,
        });
        this.batchPendingModal = false;
        this.doLoad();
      } catch (error) {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "error",
          title: "Erro",
          text: "Erro ao classificar colaboradores.",
        });
      } finally {
        this.savingBatchPending = false;
      }
    },
    doExport(format) {
      const data = this.classifiedItems.map((item) => {
        const row = {};
        this.headers.forEach((h) => {
          const value = item[h.value];
          if (h.value === "tiporh") {
            row[h.text] = value === 1 ? "Direto" : value === 2 ? "Indireto" : "Nenhum";
          } else {
            row[h.text] = value ?? "";
          }
        });
        return row;
      });

      if (format === "xlsx") {
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Tipo RH");
        XLSX.writeFile(wb, `tipo_rh_${this.selectedAnoBase}.xlsx`);
      } else {
        const headers = this.headers.map((h) => h.text).join(";");
        const rows = this.classifiedItems.map((item) => {
          return this.headers
            .map((h) => {
              const value = item[h.value];
              if (h.value === "tiporh") {
                return value === 1 ? "Direto" : value === 2 ? "Indireto" : "Nenhum";
              }
              return value ?? "";
            })
            .join(";");
        });
        const csv = [headers, ...rows].join("\n");
        const blob = new Blob(["\uFEFF" + csv], {
          type: "text/csv;charset=utf-8",
        });
        saveAs(blob, `tipo_rh_${this.selectedAnoBase}.csv`);
      }
    },
  },
};
</script>

<style scoped>
.h5 {
  font-weight: 600;
  color: #333;
}

.btn-primary {
  background-color: #1c1c44 !important;
  border-color: #1c1c44 !important;
}

.btn-primary:hover {
  background-color: #1E88E5 !important;
  border-color: #1E88E5 !important;
}
</style>
